import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * VOIHU homepage — signature scroll storytelling moment.
 *
 * Design intent (per product spec, iteration 2):
 *   • Numbers rendered in Manrope (`font-sans` + `tabular-nums`) so the
 *     digits `1`, `10`, `1,000`, `100,000` are unmistakably NUMERIC — the
 *     editorial serif made `1` read as `I` and `10` as `IO`, which broke
 *     the whole narrative. Only the copy stays in the editorial serif.
 *   • The sticky viewport is offset by the sticky-nav height so no line
 *     of a beat can be clipped at the top; `overflow-hidden` is dropped
 *     so nothing gets scissored by the container itself.
 *   • Beat transitions swap crisply (200 ms opacity + tiny Y translate)
 *     so two beats never sit legibly on top of one another.
 *   • The propagation visualization owns the full right half on desktop
 *     — a ~360-dot grid whose active count and dot size both scale with
 *     progress, so the space communicates "one → many → collective".
 *   • Total scroll length trimmed from 280vh → 200vh to remove dead
 *     zones. The narrative advances briskly.
 *   • Mobile drops the sticky/scroll-linked trick entirely and shows a
 *     compact static composition — no clipping, no trapped scroll, still
 *     conveys the story.
 */
const STEPS = [
  {
    kicker: "01",
    text: "A message can start with one person.",
    count: 1,
    countLabel: "voice",
    threshold: 0.00,
  },
  {
    kicker: "02",
    text: "But it becomes stronger when others choose to carry it forward.",
    count: 10,
    countLabel: "voices",
    threshold: 0.22,
  },
  {
    kicker: "03",
    text: "1 voice becomes 10. 10 become 1,000.",
    count: 1000,
    countLabel: "voices",
    threshold: 0.44,
  },
  {
    kicker: "04",
    text: "1,000 can become 100,000.",
    count: 100000,
    countLabel: "voices",
    threshold: 0.64,
  },
  {
    kicker: "05",
    text: "And as more people stand behind it, a message becomes harder to ignore.",
    count: 100000,
    countLabel: "voices",
    threshold: 0.82,
  },
];

// Sticky offset roughly matches the current <Nav> vertical footprint
// (py-5 + text + border ≈ 72 px). Kept as a constant so we can adjust
// it in one place if the header ever changes.
const NAV_OFFSET_PX = 72;

// Dot grid geometry. 24 × 15 = 360 cells — a solid rectangular field on
// desktop, small enough that the DOM cost is negligible.
const GRID_COLS = 24;
const GRID_ROWS = 15;
const GRID_CELLS = GRID_COLS * GRID_ROWS;

export default function HomeScrollStory() {
  const wrapRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [reduced, setReduced] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)");
    const dt = window.matchMedia("(min-width: 1024px)");
    const applyRm = () => setReduced(rm.matches);
    const applyDt = () => setIsDesktop(dt.matches);
    applyRm(); applyDt();
    rm.addEventListener?.("change", applyRm);
    dt.addEventListener?.("change", applyDt);
    return () => {
      rm.removeEventListener?.("change", applyRm);
      dt.removeEventListener?.("change", applyDt);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop) { setProgress(1); return; }
    if (reduced) { setProgress(1); return; }
    let raf = 0;
    const compute = () => {
      raf = 0;
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 800;
      const total = Math.max(1, rect.height - vh);
      // 0 when the wrapper's top hits the viewport top,
      // 1 when its bottom hits the viewport bottom.
      const p = Math.max(0, Math.min(1, -rect.top / total));
      setProgress(p);
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(compute); };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reduced, isDesktop]);

  const active = STEPS.reduce(
    (acc, s, i) => (progress >= s.threshold ? i : acc),
    0,
  );
  const climaxRevealed = progress >= 0.90;

  // Mobile: static compact version — no sticky, no scroll-linked math.
  if (!isDesktop) {
    return <MobileStory />;
  }

  // Progress → filled fraction of the grid. Rises steeply then plateaus so
  // the last stretch of the sticky range is spent on the climax reveal.
  const fill = Math.min(1, Math.pow(progress, 0.9));
  const dotsVisible = Math.max(1, Math.round(GRID_CELLS * fill));
  // Individual dot size scales from 4 px → 10 px so density AND scale
  // both grow, emphasising the "collective" feeling.
  const dotSize = 4 + Math.round(6 * fill);

  return (
    <section
      ref={wrapRef}
      data-testid="home-scroll-story"
      aria-label="Why VOIHU"
      className="relative border-t border-b oh-hairline bg-[color:var(--oh-bg)]
                 min-h-[200vh]"
    >
      <div
        className="sticky flex flex-col justify-center px-6 sm:px-10
                   max-w-[1400px] mx-auto"
        style={{
          top: `${NAV_OFFSET_PX}px`,
          height: `calc(100vh - ${NAV_OFFSET_PX}px)`,
        }}
      >
        <div className="oh-label" data-testid="story-eyebrow">
          Why VOIHU
        </div>

        <div className="mt-6 lg:mt-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* LEFT: kicker + rotating narrative beat + counter */}
          <div className="min-h-[16rem] lg:min-h-[20rem] relative">
            {STEPS.map((s, i) => (
              <div
                key={i}
                aria-hidden={i !== active}
                className={
                  "absolute inset-0 flex flex-col justify-center " +
                  "transition-all duration-200 ease-out " +
                  (i === active
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-2 pointer-events-none")
                }
              >
                <div className="oh-label text-[color:var(--oh-ink-3)]">
                  {s.kicker}
                </div>
                <p
                  className="mt-4 font-serif-editorial text-[2rem] lg:text-[3rem] xl:text-[3.5rem]
                             leading-[1.05] tracking-tight text-[color:var(--oh-ink)]
                             max-w-[560px]"
                  data-testid="story-beat"
                >
                  {s.text}
                </p>
                <div className="mt-8 flex items-baseline gap-4">
                  <span
                    className="font-medium leading-none tracking-tight
                               text-[color:var(--oh-red)]
                               text-[3.5rem] lg:text-[5rem] xl:text-[6rem]"
                    style={{
                      fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
                      fontVariantNumeric: "lining-nums tabular-nums",
                      fontFeatureSettings: "'lnum' 1, 'tnum' 1",
                    }}
                    data-testid="story-counter"
                  >
                    {s.count.toLocaleString("en-US")}
                  </span>
                  <span className="oh-label text-[color:var(--oh-ink-2)]">
                    {s.countLabel}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT: propagation grid — fills the full column, dots grow
              in count AND size with progress. */}
          <div
            className="relative w-full aspect-[24/15]"
            aria-hidden="true"
            data-testid="story-dots"
          >
            <div
              className="grid gap-2 w-full h-full"
              style={{
                gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${GRID_ROWS}, minmax(0, 1fr))`,
              }}
            >
              {Array.from({ length: GRID_CELLS }).map((_, i) => {
                const on = i < dotsVisible;
                return (
                  <span
                    key={i}
                    className="rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: on ? `${dotSize}px` : "4px",
                      height: on ? `${dotSize}px` : "4px",
                      justifySelf: "center",
                      alignSelf: "center",
                      background: on
                        ? "var(--oh-ink)"
                        : "rgba(0,0,0,0.06)",
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Climax — pinned to the bottom of the sticky viewport so it
            comes into view as scroll approaches the end. */}
        <div
          className={
            "mt-10 max-w-[780px] transition-all duration-500 " +
            (climaxRevealed
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-3")
          }
          data-testid="story-climax"
        >
          <h2 className="font-serif-editorial text-[2rem] lg:text-[3rem]
                         leading-[1.05] tracking-tight text-[color:var(--oh-ink)]">
            This is VOIHU.
          </h2>
          <p className="mt-4 text-base lg:text-lg leading-[1.65] text-[color:var(--oh-ink-2)]">
            VOIHU turns a shared message into a measurable collective voice.
            Choose a message you believe in, add your Voice, and help carry
            it to others. Every person who joins makes that voice stronger.
          </p>
          <div className="mt-6">
            <Link
              to="/why-voihu"
              className="btn-red"
              data-testid="story-cta"
            >
              See how VOIHU works →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Compact static composition shown below lg (mobile / small tablet).
 * Deliberately not sticky, not scroll-linked, and not clipped — the
 * beats stack vertically as regular content so the story is still
 * legible without any scroll-position math.
 */
function MobileStory() {
  return (
    <section
      data-testid="home-scroll-story"
      aria-label="Why VOIHU"
      className="border-t border-b oh-hairline bg-[color:var(--oh-bg)] px-6 py-16"
    >
      <div className="max-w-[560px] mx-auto">
        <div className="oh-label" data-testid="story-eyebrow">
          Why VOIHU
        </div>
        <p className="mt-6 font-serif-editorial text-[2rem] leading-[1.1] tracking-tight text-[color:var(--oh-ink)]">
          A message can start with one person. But it becomes stronger when
          others choose to carry it forward.
        </p>

        <ol className="mt-10 space-y-8" data-testid="story-beat-list">
          {[
            { count: 1,      label: "voice",  cols: 1,  rows: 1,  dot: 12, gap: 0 },
            { count: 10,     label: "voices", cols: 4,  rows: 3,  dot: 8,  gap: 6 },
            { count: 1000,   label: "voices", cols: 8,  rows: 5,  dot: 5,  gap: 4 },
            { count: 100000, label: "voices", cols: 14, rows: 8,  dot: 3,  gap: 3 },
          ].map((s, i) => (
            <li key={i} className="flex items-center gap-6">
              <div className="flex items-baseline gap-3 min-w-[9rem]">
                <span
                  className="font-medium leading-none tracking-tight
                             text-[color:var(--oh-red)] text-[2.5rem]"
                  style={{
                    fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
                    fontVariantNumeric: "lining-nums tabular-nums",
                    fontFeatureSettings: "'lnum' 1, 'tnum' 1",
                  }}
                  data-testid="story-counter"
                >
                  {s.count.toLocaleString("en-US")}
                </span>
                <span className="oh-label text-[color:var(--oh-ink-2)]">
                  {s.label}
                </span>
              </div>
              {/* Lightweight propagation cue — a small dot field that
                  visibly grows step-by-step (one → cluster → wider
                  field). No animation required for comprehension. */}
              <div
                aria-hidden="true"
                data-testid="mobile-propagation"
                className="grid shrink-0"
                style={{
                  gridTemplateColumns: `repeat(${s.cols}, minmax(0, 1fr))`,
                  gap: `${s.gap}px`,
                }}
              >
                {Array.from({ length: s.cols * s.rows }).map((_, k) => (
                  <span
                    key={k}
                    className="rounded-full bg-[color:var(--oh-ink)]"
                    style={{ width: `${s.dot}px`, height: `${s.dot}px` }}
                  />
                ))}
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-10 font-serif-editorial text-xl leading-[1.3] text-[color:var(--oh-ink)]">
          And as more people stand behind it, a message becomes harder to
          ignore.
        </p>

        <div className="mt-8" data-testid="story-climax">
          <h2 className="font-serif-editorial text-3xl leading-[1.05] tracking-tight text-[color:var(--oh-ink)]">
            This is VOIHU.
          </h2>
          <p className="mt-4 text-base leading-[1.65] text-[color:var(--oh-ink-2)]">
            VOIHU turns a shared message into a measurable collective voice.
            Choose a message you believe in, add your Voice, and help carry
            it to others.
          </p>
          <div className="mt-6">
            <Link
              to="/why-voihu"
              className="btn-red"
              data-testid="story-cta"
            >
              See how VOIHU works →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
